import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useGetList,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Download,
  RotateCcw,
  Sparkles,
  Building2,
  User,
  Briefcase,
  Calendar,
  FileText,
  Play,
  Check,
  AlertCircle,
  Search,
  GripVertical,
  X,
  ChevronDown,
  SlidersHorizontal,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useImportRollback } from "./useImportRollback";
import {
  AVAILABLE_FIELDS,
  ENTITY_CONFIG,
  autoDetectMapping,
  type EntityType,
  type FieldDefinition,
} from "./fieldDictionary";
import {
  parseSpreadsheetFile,
  downloadSampleTemplate,
  type ParsedSpreadsheet,
} from "./importParser";
import {
  executeImport,
  type DuplicateStrategy,
  type ImportProgress,
  type ImportResult,
  type ImportType,
  type ImportBatchRollbackInfo,
} from "./importEngine";
import { usePipelines } from "../deals/usePipelines";
import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  useDealCustomFields,
  CUSTOM_FIELD_TYPE_LABELS,
} from "../deals/useDealCustomFields";

type WizardStep =
  | "select_type"
  | "upload"
  | "mapping"
  | "preview"
  | "processing"
  | "completed";

interface DataImportWizardProps {
  onComplete?: () => void;
  initialEntityFilter?: EntityType;
}

// Icon map for entity types
const ENTITY_ICONS: Record<string, React.ReactNode> = {
  Building2: <Building2 className="w-3.5 h-3.5" />,
  User: <User className="w-3.5 h-3.5" />,
  Briefcase: <Briefcase className="w-3.5 h-3.5" />,
  Calendar: <Calendar className="w-3.5 h-3.5" />,
  FileText: <FileText className="w-3.5 h-3.5" />,
  X: <X className="w-3.5 h-3.5" />,
};

// ─── FIELD SIDEBAR PANEL ────────────────────────────────────────────────────

interface FieldPanelProps {
  mapping: Record<string, string>;
  onDragStart: (fieldId: string) => void;
  allFields?: FieldDefinition[];
}

const PANEL_TABS: EntityType[] = [
  "deal",
  "company",
  "person",
  "activity",
  "note",
];

const FieldSidebarPanel: React.FC<FieldPanelProps> = ({
  mapping,
  onDragStart,
  allFields = AVAILABLE_FIELDS,
}) => {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<EntityType>("deal");

  const usedFieldIds = useMemo(
    () => new Set(Object.values(mapping).filter((v) => v && v !== "ignore")),
    [mapping],
  );

  const filteredFields = useMemo(() => {
    const norm = search.toLowerCase().trim();
    return allFields.filter((f) => {
      if (f.entity === "ignore") return false;
      const matchesTab = !search ? f.entity === activeTab : true;
      const matchesSearch =
        !norm ||
        f.label.toLowerCase().includes(norm) ||
        f.aliases.some((a) => a.toLowerCase().includes(norm));
      return matchesTab && matchesSearch;
    });
  }, [allFields, search, activeTab]);

  const renderFieldItem = (field: FieldDefinition) => {
    const conf = ENTITY_CONFIG[field.entity];
    const isUsed = usedFieldIds.has(field.id);
    const isCustom = field.id.startsWith("deal.cf_");

    return (
      <div
        key={field.id}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("fieldId", field.id);
          e.dataTransfer.effectAllowed = "copy";
          onDragStart(field.id);
        }}
        className={`flex items-center gap-2.5 p-2.5 cursor-grab active:cursor-grabbing hover:bg-muted/30 transition-colors group select-none ${
          isUsed ? "opacity-50" : ""
        }`}
        title={
          field.description ||
          `Arraste para mapear esta coluna como "${field.label}"`
        }
      >
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-muted-foreground shrink-0 transition-colors" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium text-foreground truncate">
              {field.label}
            </span>
            {field.required && (
              <span className="text-destructive font-bold text-xs">*</span>
            )}
            {isCustom && (
              <span className="text-[9px] px-1 py-0.5 rounded font-medium bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                Detalhe
              </span>
            )}
            {search && (
              <span
                className={`text-[9px] px-1 rounded border font-semibold ${conf.color}`}
              >
                {conf.shortLabel}
              </span>
            )}
          </div>
          {field.description && (
            <p className="text-[10px] text-muted-foreground truncate mt-0.5">
              {field.description}
            </p>
          )}
        </div>
        {isUsed && <Check className="w-3 h-3 text-emerald-500 shrink-0" />}
      </div>
    );
  };

  const mainDealFields = useMemo(
    () => filteredFields.filter((f) => !f.id.startsWith("deal.cf_")),
    [filteredFields],
  );
  const customDealFields = useMemo(
    () => filteredFields.filter((f) => f.id.startsWith("deal.cf_")),
    [filteredFields],
  );

  return (
    <div className="flex flex-col h-full max-h-full border border-border/70 rounded-xl bg-card shadow-xs overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-border/60 bg-muted/20 shrink-0">
        <p className="text-xs font-semibold text-foreground mb-2">
          Campos de Dados do CRM
        </p>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar campo..."
            className="h-8 pl-8 text-xs bg-background"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {!search && (
        <div className="flex border-b border-border/60 bg-muted/10 overflow-x-auto shrink-0 scrollbar-none">
          {PANEL_TABS.map((tab) => {
            const conf = ENTITY_CONFIG[tab];
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold whitespace-nowrap transition-all border-b-2 cursor-pointer ${
                  activeTab === tab
                    ? "border-primary text-foreground bg-background"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30"
                }`}
              >
                {ENTITY_ICONS[conf.icon]}
                {conf.shortLabel}
              </button>
            );
          })}
        </div>
      )}

      {/* Field List */}
      <div className="flex-1 overflow-y-auto divide-y divide-border/40 min-h-0 scrollbar-thin">
        {filteredFields.length === 0 ? (
          <div className="p-4 text-center text-xs text-muted-foreground">
            Nenhum campo encontrado.
          </div>
        ) : !search && activeTab === "deal" ? (
          <>
            {mainDealFields.map(renderFieldItem)}
            {customDealFields.length > 0 && (
              <>
                <div className="bg-amber-500/10 border-y border-amber-500/20 px-3 py-1.5 flex items-center justify-between sticky top-0 z-10 backdrop-blur-xs">
                  <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1">
                    <SlidersHorizontal className="w-3 h-3" />
                    Detalhes do Negócio
                  </span>
                  <span className="text-[9px] px-1.5 py-0.2 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-300 font-semibold">
                    {customDealFields.length}
                  </span>
                </div>
                {customDealFields.map(renderFieldItem)}
              </>
            )}
          </>
        ) : (
          filteredFields.map(renderFieldItem)
        )}
      </div>

      {/* Footer tip */}
      <div className="p-2.5 border-t border-border/60 bg-muted/10 shrink-0">
        <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
          Arraste um campo e solte sobre uma coluna da planilha para mapeá-la.
        </p>
      </div>
    </div>
  );
};

// ─── COLUMN ROW (DROP TARGET) ────────────────────────────────────────────────

interface ColumnRowProps {
  header: string;
  currentFieldId: string;
  sampleVal: string;
  onFieldChange: (fieldId: string) => void;
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  allFields?: FieldDefinition[];
}

const ColumnRow: React.FC<ColumnRowProps> = ({
  header,
  currentFieldId,
  sampleVal,
  onFieldChange,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  allFields = AVAILABLE_FIELDS,
}) => {
  const selectedFieldDef = allFields.find((f) => f.id === currentFieldId);
  const entityConf = selectedFieldDef
    ? ENTITY_CONFIG[selectedFieldDef.entity]
    : ENTITY_CONFIG.ignore;
  const isMapped = currentFieldId && currentFieldId !== "ignore";
  const isDealDetail = selectedFieldDef?.id.startsWith("deal.cf_");

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex flex-col md:flex-row md:items-center gap-3 p-3.5 transition-all ${
        isDragOver
          ? "bg-primary/10 ring-1 ring-primary/50 ring-inset"
          : "hover:bg-muted/20"
      }`}
    >
      {/* Left: Column name + sample */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-xs text-foreground truncate">
            {header}
          </span>
          {isMapped && selectedFieldDef && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${entityConf.color} flex items-center gap-1`}
            >
              {ENTITY_ICONS[entityConf.icon]}
              <span>{entityConf.shortLabel}</span>
              {isDealDetail && (
                <span className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold">
                  Detalhe
                </span>
              )}
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
          Ex:{" "}
          <span className="font-mono text-foreground/80">
            {sampleVal || "(vazio)"}
          </span>
        </div>
      </div>

      {/* Arrow */}
      <div className="hidden md:flex items-center justify-center text-muted-foreground px-1">
        <ArrowRight className="w-4 h-4" />
      </div>

      {/* Right: Mapped field pill OR drop hint */}
      <div className="w-full md:w-72 flex items-center gap-2">
        {isDragOver ? (
          <div className="flex-1 h-9 rounded-lg border-2 border-dashed border-primary/60 bg-primary/5 flex items-center justify-center text-[11px] text-primary font-medium">
            Solte aqui para mapear
          </div>
        ) : (
          <div
            className={`flex-1 h-9 rounded-lg border flex items-center px-3 gap-2 text-xs cursor-pointer transition-all group ${
              isMapped
                ? `${entityConf.color} font-medium`
                : "border-border/60 text-muted-foreground hover:border-primary/50 hover:bg-muted/30"
            }`}
            title="Arraste um campo do painel direito, ou clique para abrir o seletor"
          >
            {isMapped ? (
              <>
                <span className="shrink-0">
                  {ENTITY_ICONS[entityConf.icon]}
                </span>
                <span className="flex-1 truncate">
                  {selectedFieldDef?.label}
                </span>
                {isDealDetail && (
                  <span className="shrink-0 text-[9px] px-1 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 font-semibold">
                    Detalhe
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onFieldChange("ignore");
                  }}
                  className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </>
            ) : (
              <>
                <GripVertical className="w-3.5 h-3.5 opacity-40" />
                <span className="flex-1">Arraste um campo aqui...</span>
              </>
            )}
          </div>
        )}

        {/* Select dropdown fallback */}
        <ColumnFieldSelect
          currentFieldId={currentFieldId}
          onFieldChange={onFieldChange}
          allFields={allFields}
        />
      </div>
    </div>
  );
};

// Compact select dropdown for keyboard/manual selection
interface ColumnFieldSelectProps {
  currentFieldId: string;
  onFieldChange: (fieldId: string) => void;
  allFields?: FieldDefinition[];
}

const ColumnFieldSelect: React.FC<ColumnFieldSelectProps> = ({
  currentFieldId,
  onFieldChange,
  allFields = AVAILABLE_FIELDS,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const norm = search.toLowerCase().trim();
    return allFields.filter(
      (f) =>
        !norm ||
        f.label.toLowerCase().includes(norm) ||
        f.aliases.some((a) => a.toLowerCase().includes(norm)),
    );
  }, [allFields, search]);

  // Close on outside click
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
      setSearch("");
    }
  }, []);

  const grouped = useMemo(() => {
    const map: Partial<Record<EntityType, FieldDefinition[]>> = {};
    for (const f of filtered) {
      if (!map[f.entity]) map[f.entity] = [];
      map[f.entity]!.push(f);
    }
    return map;
  }, [filtered]);

  const entityOrder: EntityType[] = [
    "deal",
    "company",
    "person",
    "activity",
    "note",
  ];

  const renderFieldOption = (f: FieldDefinition) => {
    const isCustom = f.id.startsWith("deal.cf_");
    return (
      <button
        key={f.id}
        onMouseDown={() => {
          onFieldChange(f.id);
          setOpen(false);
          setSearch("");
        }}
        className={`w-full px-3 py-2 text-left text-xs hover:bg-muted/40 flex items-center justify-between gap-2 transition-colors ${
          currentFieldId === f.id
            ? "bg-primary/5 text-primary font-medium"
            : "text-foreground"
        }`}
      >
        <span className="flex-1 truncate">{f.label}</span>
        {isCustom && (
          <span className="text-[9px] px-1 py-0.5 rounded font-medium bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 shrink-0">
            Detalhe
          </span>
        )}
        {f.required && (
          <span className="text-destructive font-bold shrink-0">*</span>
        )}
        {currentFieldId === f.id && (
          <Check className="w-3 h-3 text-primary shrink-0" />
        )}
      </button>
    );
  };

  return (
    <div ref={ref} className="relative shrink-0" onBlur={handleBlur}>
      <button
        onClick={() => {
          setOpen((v) => !v);
          setSearch("");
        }}
        className="h-9 w-9 rounded-lg border border-border/60 bg-background hover:bg-muted/40 flex items-center justify-center text-muted-foreground hover:text-foreground transition-all cursor-pointer"
        title="Selecionar campo manualmente"
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-popover border border-border rounded-xl shadow-xl z-50 flex flex-col max-h-80 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border/60">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar..."
                className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-border/60 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          {/* Options */}
          <div className="overflow-y-auto flex-1">
            <button
              onMouseDown={() => {
                onFieldChange("ignore");
                setOpen(false);
                setSearch("");
              }}
              className="w-full px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/40 flex items-center gap-2"
            >
              <X className="w-3.5 h-3.5" />
              Não importar esta coluna
            </button>

            {entityOrder.map((ent) => {
              const fields = grouped[ent];
              if (!fields?.length) return null;
              const conf = ENTITY_CONFIG[ent];

              const mainFields =
                ent === "deal"
                  ? fields.filter((f) => !f.id.startsWith("deal.cf_"))
                  : fields;
              const customFields =
                ent === "deal"
                  ? fields.filter((f) => f.id.startsWith("deal.cf_"))
                  : [];

              return (
                <div key={ent}>
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30 flex items-center gap-1.5">
                    {ENTITY_ICONS[conf.icon]}
                    {conf.label}
                  </div>
                  {mainFields.map(renderFieldOption)}
                  {customFields.length > 0 && (
                    <>
                      <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 bg-amber-500/15 flex items-center justify-between border-t border-border/50">
                        <span className="flex items-center gap-1">
                          <SlidersHorizontal className="w-3 h-3" />
                          Detalhes do Negócio
                        </span>
                        <span className="text-[9px] font-normal lowercase">
                          personalizados
                        </span>
                      </div>
                      {customFields.map(renderFieldOption)}
                    </>
                  )}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                Nenhum campo encontrado.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN WIZARD COMPONENT ────────────────────────────────────────────────────

export const DataImportWizard: React.FC<DataImportWizardProps> = ({
  onComplete,
  initialEntityFilter,
}) => {
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { pipelines, currentPipeline } = usePipelines();
  const { dealStages } = useConfigurationContext();
  const { fields: dealCustomFields } = useDealCustomFields();
  const { data: salesData } = useGetList("sales", {
    pagination: { page: 1, perPage: 100 },
  });
  const salesList = salesData || [];

  // Deal Options and Defaults
  const [selectedSalesId, setSelectedSalesId] = useState<string>("");
  const [defaultOrigem, setDefaultOrigem] = useState<string>("");
  const [defaultTemperatura, setDefaultTemperatura] = useState<string>("morno");
  const [customFieldsDefaults, setCustomFieldsDefaults] = useState<
    Record<string, any>
  >({});

  const customFieldDefs: FieldDefinition[] = useMemo(() => {
    return (dealCustomFields || []).map((cf) => {
      const fieldKey = cf.id.startsWith("cf_") ? cf.id : `cf_${cf.id}`;
      return {
        id: `deal.${fieldKey}`,
        entity: "deal" as EntityType,
        label: cf.name,
        description: `Detalhe do Negócio (${CUSTOM_FIELD_TYPE_LABELS[cf.type] || cf.type})`,
        aliases: [
          cf.name.toLowerCase(),
          `detalhe ${cf.name.toLowerCase()}`,
          `detalhes ${cf.name.toLowerCase()}`,
          cf.id.replace(/^cf_/, "").replace(/_/g, " "),
          cf.id.toLowerCase(),
        ],
      };
    });
  }, [dealCustomFields]);

  const allFields: FieldDefinition[] = useMemo(() => {
    return [...AVAILABLE_FIELDS, ...customFieldDefs];
  }, [customFieldDefs]);

  // Determine initial step and type from URL param
  const initialImportType: ImportType =
    initialEntityFilter === "person" ||
    initialEntityFilter === "company" ||
    initialEntityFilter === "deal"
      ? (initialEntityFilter as ImportType)
      : (undefined as unknown as ImportType);

  const [step, setStep] = useState<WizardStep>(
    initialImportType ? "upload" : "select_type",
  );
  const [importType, setImportType] = useState<ImportType>(
    initialImportType ?? "deal",
  );
  const [parsedData, setParsedData] = useState<ParsedSpreadsheet | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [dragOverHeader, setDragOverHeader] = useState<string | null>(null);

  // Business rules
  const [duplicateStrategy, setDuplicateStrategy] =
    useState<DuplicateStrategy>("merge");
  const [selectedFunilId, setSelectedFunilId] = useState<string>(
    () => currentPipeline?.id || pipelines[0]?.id || "principal",
  );
  const [selectedStageId, setSelectedStageId] = useState<string>(
    () =>
      currentPipeline?.etapas?.[0]?.value ||
      pipelines[0]?.etapas?.[0]?.value ||
      "prospeccao",
  );

  // Sincroniza pipeline e etapa inicial quando os funis carregam
  useEffect(() => {
    if (pipelines.length > 0) {
      if (!pipelines.some((p) => p.id === selectedFunilId)) {
        const fallbackPipe = currentPipeline || pipelines[0];
        setSelectedFunilId(fallbackPipe.id);
        if (fallbackPipe.etapas?.[0]?.value) {
          setSelectedStageId(fallbackPipe.etapas[0].value);
        }
      }
    }
  }, [pipelines, currentPipeline, selectedFunilId]);

  const handleFunilChange = (newFunilId: string) => {
    setSelectedFunilId(newFunilId);
    const pipe = pipelines.find((p) => p.id === newFunilId);
    if (pipe?.etapas?.[0]?.value) {
      setSelectedStageId(pipe.etapas[0].value);
    }
  };

  // Execution state
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [undoTargetBatch, setUndoTargetBatch] =
    useState<ImportBatchRollbackInfo | null>(null);
  const {
    lastImport,
    hasLastImport,
    undoLastImport,
    isUndoing: isRollbackUndoing,
  } = useImportRollback();

  const handleConfirmUndo = async () => {
    if (undoTargetBatch) {
      const ok = await undoLastImport(undoTargetBatch);
      if (ok) {
        setUndoTargetBatch(null);
        handleResetWizard();
      }
    }
  };

  const activePipeline =
    pipelines.find((p) => p.id === selectedFunilId) ||
    currentPipeline ||
    pipelines[0];
  const availableStages =
    activePipeline?.etapas ||
    dealStages.map((s) => ({ value: s.value, label: s.label }));

  // ── Step 1: File Upload ──────────────────────────────────────────────────
  const handleFileSelected = async (file: File) => {
    try {
      setIsReadingFile(true);
      const parsed = await parseSpreadsheetFile(file);
      setParsedData(parsed);

      const initialMapping = autoDetectMapping(
        parsed.headers,
        allFields,
        importType,
      );
      setMapping(initialMapping);

      setStep("mapping");
      notify(
        `Planilha carregada: ${parsed.totalRows.toLocaleString("pt-BR")} linhas encontradas!`,
        { type: "info" },
      );
    } catch (err: unknown) {
      notify((err as Error).message || "Erro ao ler a planilha.", {
        type: "error",
      });
    } finally {
      setIsReadingFile(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFileSelected(e.dataTransfer.files[0]);
  };

  // ── Step 2: Column Mapping ───────────────────────────────────────────────
  const handleFieldChange = (columnHeader: string, newFieldId: string) => {
    setMapping((prev) => ({ ...prev, [columnHeader]: newFieldId }));
  };

  // Drag-and-drop handlers for column rows
  const handleColumnDragOver = (e: React.DragEvent, header: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOverHeader(header);
  };

  const handleColumnDrop = (e: React.DragEvent, header: string) => {
    e.preventDefault();
    const fieldId = e.dataTransfer.getData("fieldId");
    if (fieldId) {
      handleFieldChange(header, fieldId);
    }
    setDragOverHeader(null);
  };

  const mappedCount = useMemo(
    () => Object.values(mapping).filter((f) => f && f !== "ignore").length,
    [mapping],
  );

  const hasRequiredName = useMemo(() => {
    const values = Object.values(mapping);
    if (importType === "company") {
      return (
        values.includes("company.name") || values.includes("deal.company_name")
      );
    }
    if (importType === "person") {
      return (
        values.includes("person.full_name") ||
        values.includes("person.first_name") ||
        values.includes("deal.contact_name") ||
        values.includes("person.email_work") ||
        values.includes("person.phone_work") ||
        values.includes("deal.contact_email") ||
        values.includes("deal.contact_phone")
      );
    }
    // deal: qualquer identificador de negócio, contato ou empresa é suficiente
    return (
      values.includes("deal.name") ||
      values.includes("company.name") ||
      values.includes("deal.company_name") ||
      values.includes("person.full_name") ||
      values.includes("person.first_name") ||
      values.includes("deal.contact_name") ||
      values.includes("person.email_work") ||
      values.includes("person.phone_work") ||
      values.includes("deal.contact_email") ||
      values.includes("deal.contact_phone") ||
      values.includes("deal.amount")
    );
  }, [mapping, importType]);

  const hasDealMapped = useMemo(() => {
    const values = Object.values(mapping);
    return values.some((v) => v && v.startsWith("deal."));
  }, [mapping]);

  // ── Step 4: Import Execution ─────────────────────────────────────────────
  const handleStartImport = async () => {
    if (!parsedData) return;
    setStep("processing");

    try {
      const res = await executeImport(parsedData, {
        mapping,
        duplicateStrategy,
        importType,
        pipelines,
        defaultFunilId: selectedFunilId,
        defaultStageId: selectedStageId,
        defaultSalesId: selectedSalesId || identity?.id || null,
        defaultOrigem: defaultOrigem || null,
        defaultTemperatura: defaultTemperatura || "morno",
        dealCustomFieldsDefaults: customFieldsDefaults,
        dataProvider,
        onProgress: (p) => setProgress(p),
      });

      setResult(res);
      setStep("completed");

      // Invalida caches do React Query para que Deals, Contatos, Empresas e Tarefas apareçam imediatamente no Kanban e listas
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["activity"] });

      if (res.hasErrors) {
        notify(
          `Importação concluída com avisos: ${res.processed} linhas importadas, ${res.skippedCount} ignoradas.`,
          { type: "warning" },
        );
      } else {
        notify(`Sucesso! ${res.processed} linhas importadas perfeitamente.`, {
          type: "success",
        });
      }
      onComplete?.();
    } catch (err: unknown) {
      notify("Erro durante a importação: " + (err as Error).message, {
        type: "error",
      });
      setStep("preview");
    }
  };

  const handleDownloadSkipFile = () => {
    if (!result?.skippedCsvContent) return;
    const blob = new Blob(["\uFEFF" + result.skippedCsvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `linhas_ignoradas_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleResetWizard = () => {
    setParsedData(null);
    setMapping({});
    setProgress(null);
    setResult(null);
    setStep("select_type");
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6 py-4">
      {/* ── Header + Stepper ── */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-primary" />
              Área de Importação
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Importe Pessoas, Organizações e Oportunidades vinculados
              automaticamente a partir de uma única planilha.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Botão Importação */}
            <Button
              variant={
                step === "upload" || step === "select_type"
                  ? "default"
                  : "outline"
              }
              size="sm"
              onClick={handleResetWizard}
              className="gap-2 text-xs cursor-pointer h-9 font-medium"
            >
              <Upload className="w-4 h-4" />
              Importação
            </Button>

            {/* Botão Desfazer */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUndoTargetBatch(lastImport)}
              disabled={!hasLastImport || isRollbackUndoing}
              title={
                hasLastImport
                  ? `Desfazer última importação (${lastImport?.totalCreated} registros de "${lastImport?.fileName}")`
                  : "Nenhuma importação recente para desfazer"
              }
              className={`gap-1.5 text-xs cursor-pointer h-9 transition-colors ${
                hasLastImport
                  ? "text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 border-rose-300 dark:border-rose-900/50 font-medium"
                  : "opacity-40"
              }`}
            >
              {isRollbackUndoing ? (
                <Sparkles className="w-3.5 h-3.5 animate-spin text-rose-600" />
              ) : (
                <RotateCcw className="w-3.5 h-3.5" />
              )}
              <span>Desfazer</span>
              {hasLastImport && lastImport?.totalCreated ? (
                <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded-full bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/20">
                  {lastImport.totalCreated}
                </span>
              ) : null}
            </Button>
          </div>
        </div>

        {hasLastImport &&
          lastImport &&
          (step === "select_type" || step === "upload") && (
            <div className="flex items-center justify-between p-3 rounded-lg border border-rose-500/20 bg-rose-500/5 text-xs">
              <div className="flex items-center gap-2 text-muted-foreground">
                <RotateCcw className="w-4 h-4 text-rose-500 shrink-0" />
                <span>
                  Última importação realizada:{" "}
                  <strong className="text-foreground">
                    {lastImport.fileName}
                  </strong>{" "}
                  ({lastImport.totalCreated} registros criados em{" "}
                  {new Date(lastImport.timestamp).toLocaleString("pt-BR")})
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setUndoTargetBatch(lastImport)}
                disabled={isRollbackUndoing}
                className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 cursor-pointer h-7 px-2 font-medium"
              >
                Desfazer Agora
              </Button>
            </div>
          )}

        {/* Stepper - only show after type is selected */}
        {step !== "select_type" && (
          <div className="flex items-center justify-between border rounded-lg bg-card/60 p-2 mt-2 overflow-x-auto gap-1">
            {[
              { id: "upload", label: "1. Envio" },
              { id: "mapping", label: "2. Mapeamento" },
              { id: "preview", label: "3. Prévia & Regras" },
              { id: "processing", label: "4. Execução" },
              { id: "completed", label: "5. Conclusão" },
            ].map((item, idx) => {
              const isCurrent = step === item.id;
              const isDone =
                (step === "mapping" && idx === 0) ||
                (step === "preview" && idx <= 1) ||
                (step === "processing" && idx <= 2) ||
                (step === "completed" && idx <= 3);

              return (
                <div
                  key={item.id}
                  className="flex items-center gap-2 text-xs font-medium shrink-0"
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${
                      isDone
                        ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40"
                        : isCurrent
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isDone ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                  </span>
                  <span
                    className={`hidden sm:inline ${isCurrent ? "font-semibold text-foreground" : "text-muted-foreground"}`}
                  >
                    {item.label}
                  </span>
                  {idx < 4 && (
                    <div className="hidden md:block w-6 h-[1px] bg-border mx-1" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ETAPA 0: SELEÇÃO DO TIPO DE ENTIDADE                              */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {step === "select_type" && (
        <div className="flex flex-col gap-6">
          <div className="text-center space-y-1.5 py-2">
            <p className="text-sm font-semibold text-foreground">
              O que você deseja importar?
            </p>
            <p className="text-xs text-muted-foreground">
              Selecione o tipo de dado principal da sua planilha.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card: Pessoa */}
            <button
              onClick={() => {
                setImportType("person");
                setStep("upload");
              }}
              className="group relative flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-border/70 bg-card hover:border-emerald-500/60 hover:bg-emerald-500/5 transition-all duration-200 text-left cursor-pointer shadow-xs hover:shadow-md"
            >
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <User className="w-7 h-7 text-emerald-600" />
              </div>
              <div className="space-y-1.5 text-center">
                <p className="text-base font-bold text-foreground">Pessoas</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Contatos, leads e clientes. E-mail e telefone são opcionais —
                  apenas o nome é obrigatório.
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {["Nome", "E-mail", "Telefone", "Empresa", "Cargo"].map((t) => (
                  <span
                    key={t}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-medium"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </button>

            {/* Card: Organização */}
            <button
              onClick={() => {
                setImportType("company");
                setStep("upload");
              }}
              className="group relative flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-border/70 bg-card hover:border-blue-500/60 hover:bg-blue-500/5 transition-all duration-200 text-left cursor-pointer shadow-xs hover:shadow-md"
            >
              <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <Building2 className="w-7 h-7 text-blue-600" />
              </div>
              <div className="space-y-1.5 text-center">
                <p className="text-base font-bold text-foreground">
                  Organizações
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Empresas, clientes PJ e parceiros. Apenas a Razão Social é
                  obrigatória.
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {["Razão Social", "CNPJ", "Setor", "Cidade", "Telefone"].map(
                  (t) => (
                    <span
                      key={t}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-400 font-medium"
                    >
                      {t}
                    </span>
                  ),
                )}
              </div>
            </button>

            {/* Card: Negócio */}
            <button
              onClick={() => {
                setImportType("deal");
                setStep("upload");
              }}
              className="group relative flex flex-col items-center gap-4 p-6 rounded-xl border-2 border-border/70 bg-card hover:border-amber-500/60 hover:bg-amber-500/5 transition-all duration-200 text-left cursor-pointer shadow-xs hover:shadow-md"
            >
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <Briefcase className="w-7 h-7 text-amber-600" />
              </div>
              <div className="space-y-1.5 text-center">
                <p className="text-base font-bold text-foreground">Negócios</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Oportunidades no Funil de Vendas. Cria automaticamente a
                  Pessoa e a Organização vinculadas.
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {["Título", "Valor", "Etapa", "Pessoa", "Empresa"].map((t) => (
                  <span
                    key={t}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 font-medium"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ETAPA 1: UPLOAD                                                     */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {step === "upload" && (
        <Card className="border-border/80 shadow-xs">
          <CardHeader className="pb-4">
            {/* Type indicator banner */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                    importType === "person"
                      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                      : importType === "company"
                        ? "bg-blue-500/10 text-blue-700 dark:text-blue-400"
                        : "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                  }`}
                >
                  {importType === "person" && <User className="w-3.5 h-3.5" />}
                  {importType === "company" && (
                    <Building2 className="w-3.5 h-3.5" />
                  )}
                  {importType === "deal" && (
                    <Briefcase className="w-3.5 h-3.5" />
                  )}
                  Importando:{" "}
                  {importType === "person"
                    ? "Pessoas"
                    : importType === "company"
                      ? "Organizações"
                      : "Negócios"}
                </span>
              </div>
              <button
                onClick={() => setStep("select_type")}
                className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 cursor-pointer"
              >
                <ArrowLeft className="w-3 h-3" />
                Trocar tipo
              </button>
            </div>
            <CardTitle className="text-base">
              Selecione ou arraste sua planilha
            </CardTitle>
            <CardDescription className="text-xs">
              Aceitamos arquivos nos formatos <strong>.xlsx</strong>,{" "}
              <strong>.xls</strong> ou <strong>.csv</strong> com até 50.000
              linhas e até 50 MB.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Dropzone */}
            <div
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed border-border/80 hover:border-primary/60 rounded-xl p-8 text-center flex flex-col items-center justify-center gap-3 bg-muted/20 hover:bg-muted/30 transition-all cursor-pointer"
              onClick={() =>
                document.getElementById("file-input-spreadsheet")?.click()
              }
            >
              <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center shadow-xs">
                {isReadingFile ? (
                  <Sparkles className="w-7 h-7 animate-spin" />
                ) : (
                  <Upload className="w-7 h-7" />
                )}
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  {isReadingFile
                    ? "Lendo e validando a planilha..."
                    : "Clique para selecionar ou arraste o arquivo aqui"}
                </p>
                <p className="text-xs text-muted-foreground">
                  XLSX, XLS ou CSV (uma aba só, com linha de cabeçalho)
                </p>
              </div>
              <input
                id="file-input-spreadsheet"
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                disabled={isReadingFile}
                onChange={(e) => {
                  if (e.target.files?.[0])
                    handleFileSelected(e.target.files[0]);
                }}
              />
            </div>

            {/* Template Downloads */}
            <div className="p-4 rounded-lg bg-card border border-border/70 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="space-y-0.5 text-center sm:text-left">
                <p className="text-xs font-semibold text-foreground">
                  Não tem uma planilha pronta?
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Baixe nosso modelo oficial com colunas pré-configuradas para{" "}
                  {importType === "person"
                    ? "Pessoas"
                    : importType === "company"
                      ? "Organizações"
                      : "Negócios"}
                  :
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => downloadSampleTemplate("xlsx", importType)}
                  className="text-xs gap-1.5 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  Modelo Excel (.xlsx)
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => downloadSampleTemplate("csv", importType)}
                  className="text-xs gap-1.5 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  Modelo CSV
                </Button>
              </div>
            </div>

            {/* Tips */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-muted-foreground pt-2 border-t border-border/50">
              <div className="p-2.5 rounded-md bg-muted/30">
                <strong className="text-foreground block mb-0.5">
                  Vínculo Automático
                </strong>
                Se a mesma linha tiver dados de Pessoa, Empresa e Valor, o CRM
                cria os três já interligados.
              </div>
              <div className="p-2.5 rounded-md bg-muted/30">
                <strong className="text-foreground block mb-0.5">
                  Campos Obrigatórios
                </strong>
                Pessoas e Empresas precisam de nome; Negócios precisam de uma
                Pessoa ou Empresa vinculada.
              </div>
              <div className="p-2.5 rounded-md bg-muted/30">
                <strong className="text-foreground block mb-0.5">
                  Evite Duplicatas
                </strong>
                Inclua e-mail para Pessoas e CNPJ para Empresas para que o
                sistema possa mesclar registros.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ETAPA 2: MAPEAMENTO DE COLUNAS (duas colunas + painel lateral)     */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {step === "mapping" && parsedData && (
        <div className="flex flex-col gap-4">
          {/* Header row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-foreground">
                Mapeamento de Colunas
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mapeamos automaticamente as colunas pelo nome. Arraste campos do
                painel direito para ajustar ou use o seletor (
                <ChevronDown className="w-3 h-3 inline" />
                ).
              </p>
            </div>
            <Badge variant="outline" className="text-xs px-2.5 py-1 shrink-0">
              <strong>{mappedCount}</strong>&nbsp;de&nbsp;
              {parsedData.headers.length} colunas mapeadas
            </Badge>
          </div>

          {/* Two-column layout: columns list + field sidebar */}
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            {/* Left: Column rows (drop targets) */}
            <Card className="flex-1 border-border/80 shadow-xs min-w-0 overflow-hidden">
              <CardHeader className="py-2.5 px-4 border-b border-border/60 bg-muted/10">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Colunas da Planilha</CardTitle>
                  <span className="text-[11px] text-muted-foreground">
                    {parsedData.totalRows.toLocaleString("pt-BR")} linhas no
                    arquivo
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-border/50">
                  {parsedData.headers.map((header, idx) => {
                    const currentFieldId = mapping[header] || "ignore";
                    const sampleVal = String(
                      parsedData.rawRows[0]?.[header] ?? "",
                    );
                    return (
                      <ColumnRow
                        key={idx}
                        header={header}
                        currentFieldId={currentFieldId}
                        sampleVal={sampleVal}
                        onFieldChange={(fieldId) =>
                          handleFieldChange(header, fieldId)
                        }
                        isDragOver={dragOverHeader === header}
                        onDragOver={(e) => handleColumnDragOver(e, header)}
                        onDragLeave={() => setDragOverHeader(null)}
                        onDrop={(e) => handleColumnDrop(e, header)}
                        allFields={allFields}
                      />
                    );
                  })}
                </div>

                {/* Footer actions */}
                <div className="p-4 border-t border-border/60 bg-muted/10 flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStep("upload")}
                    className="text-xs gap-1.5 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Voltar
                  </Button>

                  <div className="flex items-center gap-3">
                    {!hasRequiredName && (
                      <span className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Mapeie ao menos um campo identificador (Negócio, Contato
                        ou Empresa).
                      </span>
                    )}
                    <Button
                      size="sm"
                      disabled={!hasRequiredName}
                      onClick={() => setStep("preview")}
                      className="text-xs gap-1.5 cursor-pointer font-semibold"
                    >
                      Avançar para Prévia
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Right: Field sidebar panel */}
            <div className="w-full lg:w-80 shrink-0 lg:sticky lg:top-4 self-start h-[calc(60vh+95px)] min-h-[460px] max-h-[calc(100vh-140px)] flex flex-col">
              <FieldSidebarPanel
                mapping={mapping}
                onDragStart={() => {}}
                allFields={allFields}
              />
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ETAPA 3: PRÉVIA & CONFIGURAÇÕES                                    */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {step === "preview" && parsedData && (
        <Card className="border-border/80 shadow-xs">
          <CardHeader className="pb-3 border-b border-border/60">
            <CardTitle className="text-base">
              Prévia dos Registros e Regras de Importação
            </CardTitle>
            <CardDescription className="text-xs">
              Confira como os dados serão estruturados no CRM antes de iniciar o
              processo de gravação.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5 space-y-6">
            {/* Preview rows */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Amostra dos 3 Primeiros Registros:
                </h3>
                <span className="text-[11px] text-muted-foreground">
                  Total: {parsedData.totalRows.toLocaleString("pt-BR")} linhas
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {parsedData.rawRows.slice(0, 3).map((r, idx) => {
                  let pName = "",
                    cName = "",
                    email = "",
                    phone = "",
                    dealName = "",
                    dealVal = "",
                    ownerName = "";

                  Object.entries(mapping).forEach(([h, fId]) => {
                    const val = r[h];
                    if (!val) return;
                    if (
                      fId === "person.full_name" ||
                      fId === "person.first_name" ||
                      fId === "deal.contact_name"
                    )
                      pName = val;
                    if (fId === "company.name" || fId === "deal.company_name")
                      cName = val;
                    if (
                      fId === "person.email_work" ||
                      fId === "person.email_home" ||
                      fId === "deal.contact_email"
                    )
                      email = val;
                    if (
                      fId === "person.phone_work" ||
                      fId === "person.phone_home" ||
                      fId === "deal.contact_phone"
                    )
                      phone = val;
                    if (fId === "deal.name") dealName = val;
                    if (fId === "deal.amount") dealVal = val;
                    if (
                      fId === "deal.owner_name" ||
                      fId === "person.owner_name" ||
                      fId === "company.owner_name"
                    )
                      ownerName = val;
                  });

                  // Detalhes do Negócio mapeados na linha
                  const customDetailBadges: string[] = [];
                  Object.entries(mapping).forEach(([h, fId]) => {
                    if (
                      fId &&
                      (fId.startsWith("deal.cf_") ||
                        fId.startsWith("deal.custom_fields.")) &&
                      r[h]
                    ) {
                      const cfDef = allFields.find((f) => f.id === fId);
                      if (cfDef)
                        customDetailBadges.push(`${cfDef.label}: ${r[h]}`);
                    }
                  });

                  if (!pName && (email || phone)) {
                    pName = email ? email.split("@")[0] : `Contato (${phone})`;
                  }

                  if (importType === "deal" || dealName) {
                    if (!dealName && (pName || cName)) {
                      dealName =
                        pName && cName
                          ? `${pName} - ${cName}`
                          : cName
                            ? `Oportunidade - ${cName}`
                            : `Oportunidade - ${pName}`;
                    }
                  }

                  return (
                    <div
                      key={idx}
                      className="p-3 rounded-lg border border-border/80 bg-muted/20 space-y-2 text-xs"
                    >
                      <div className="font-semibold text-primary">
                        Registro #{idx + 1}
                      </div>
                      {cName && (
                        <div className="flex items-start gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                          <span className="truncate">
                            <strong>Empresa:</strong> {cName}
                          </span>
                        </div>
                      )}
                      {pName && (
                        <div className="flex items-start gap-1.5">
                          <User className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                          <span className="truncate">
                            <strong>Contato:</strong> {pName}
                          </span>
                        </div>
                      )}
                      {email && (
                        <div className="text-[11px] text-muted-foreground truncate pl-5">
                          {email} {phone ? `• ${phone}` : ""}
                        </div>
                      )}
                      {dealName && (importType === "deal" || hasDealMapped) && (
                        <div className="flex items-start gap-1.5 pt-1 border-t border-border/50 text-[11px]">
                          <Briefcase className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                          <span className="truncate">
                            <strong>Negócio:</strong> {dealName}{" "}
                            {dealVal ? `(R$ ${dealVal})` : ""}
                          </span>
                        </div>
                      )}
                      {ownerName && (
                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <User className="w-3 h-3 shrink-0" />
                          Responsável: <strong>{ownerName}</strong>
                        </div>
                      )}
                      {customDetailBadges.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1 border-t border-border/40">
                          {customDetailBadges.slice(0, 3).map((b, i) => (
                            <span
                              key={i}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 font-medium"
                            >
                              {b}
                            </span>
                          ))}
                          {customDetailBadges.length > 3 && (
                            <span className="text-[10px] text-muted-foreground self-center">
                              +{customDetailBadges.length - 3} detalhes
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pipeline & Stage defaults — only for deal imports */}
            {(importType === "deal" || hasDealMapped) && (
              <div className="p-4 rounded-lg bg-card border border-border/70 space-y-4">
                <div className="space-y-0.5">
                  <h3 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <Briefcase className="w-4 h-4 text-amber-500" />
                    Funil e Etapa Padrão para os Negócios
                  </h3>
                  <p className="text-[11px] text-muted-foreground">
                    Define onde cada oportunidade será criada no Kanban se a
                    planilha não especificar.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border/50">
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground">
                      Funil de Destino:
                    </label>
                    <Select
                      value={selectedFunilId}
                      onValueChange={handleFunilChange}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Selecione o Funil..." />
                      </SelectTrigger>
                      <SelectContent>
                        {pipelines.map((p) => (
                          <SelectItem
                            key={p.id}
                            value={p.id}
                            className="text-xs"
                          >
                            {p.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground">
                      Etapa Inicial Padrão:
                    </label>
                    <Select
                      value={selectedStageId}
                      onValueChange={setSelectedStageId}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Selecione a Etapa..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStages.map((s) => (
                          <SelectItem
                            key={s.value}
                            value={s.value}
                            className="text-xs"
                          >
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Opções e Detalhes do Negócio */}
            {(importType === "deal" || hasDealMapped) && (
              <div className="p-4 rounded-lg bg-card border border-border/70 space-y-4">
                <div className="space-y-0.5">
                  <h3 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <SlidersHorizontal className="w-4 h-4 text-primary" />
                    Opções e Detalhes do Negócio
                  </h3>
                  <p className="text-[11px] text-muted-foreground">
                    Configure os responsáveis e valores padrão para os detalhes
                    do negócio que não estiverem na planilha.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-border/50">
                  {/* Responsável Padrão */}
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground">
                      Responsável Padrão:
                    </label>
                    <Select
                      value={selectedSalesId || "current_user"}
                      onValueChange={(val) =>
                        setSelectedSalesId(val === "current_user" ? "" : val)
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Responsável..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="current_user" className="text-xs">
                          {identity?.fullName || identity?.name
                            ? `Usuário Atual (${identity.fullName || identity.name})`
                            : "Usuário Atual Logado"}
                        </SelectItem>
                        {(salesList || []).map((s: any) => (
                          <SelectItem
                            key={s.id}
                            value={String(s.id)}
                            className="text-xs"
                          >
                            {s.first_name} {s.last_name || ""}{" "}
                            {s.email ? `(${s.email})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Origem Padrão */}
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground">
                      Origem Padrão do Lead:
                    </label>
                    <Input
                      value={defaultOrigem}
                      onChange={(e) => setDefaultOrigem(e.target.value)}
                      placeholder="Ex: Planilha, Indicação..."
                      className="h-8 text-xs"
                    />
                  </div>

                  {/* Temperatura Padrão */}
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground">
                      Temperatura Padrão:
                    </label>
                    <Select
                      value={defaultTemperatura}
                      onValueChange={setDefaultTemperatura}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morno" className="text-xs">
                          Morno
                        </SelectItem>
                        <SelectItem value="quente" className="text-xs">
                          Quente
                        </SelectItem>
                        <SelectItem value="frio" className="text-xs">
                          Frio
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Campos Personalizados (Detalhes do Negócio) */}
                {dealCustomFields.length > 0 && (
                  <div className="pt-3 border-t border-border/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-foreground flex items-center gap-1.5">
                        Valores Padrão para os Detalhes do Negócio
                        <Badge
                          variant="secondary"
                          className="text-[9px] px-1 py-0"
                        >
                          {dealCustomFields.length}{" "}
                          {dealCustomFields.length === 1 ? "campo" : "campos"}
                        </Badge>
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Preenchido caso a linha da planilha não possua este
                        detalhe
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {dealCustomFields.map((cf) => {
                        const expectedFieldKey = cf.id.startsWith("cf_")
                          ? cf.id
                          : `cf_${cf.id}`;
                        const isMapped = Object.values(mapping).some(
                          (m) =>
                            m === `deal.${expectedFieldKey}` ||
                            m === `deal.cf_${cf.id}` ||
                            m === `deal.${cf.id}` ||
                            m === `deal.custom_fields.${cf.id}` ||
                            m === `deal.custom_fields.${expectedFieldKey}`,
                        );
                        const val = customFieldsDefaults[cf.id] ?? "";

                        return (
                          <div key={cf.id} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <label
                                className="text-[11px] font-medium text-muted-foreground truncate"
                                title={cf.name}
                              >
                                {cf.name}
                              </label>
                              {isMapped && (
                                <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-medium">
                                  Mapeado
                                </span>
                              )}
                            </div>

                            {cf.type === "select" ||
                            cf.type === "multiselect" ? (
                              <Select
                                value={val || "__empty__"}
                                onValueChange={(newV) =>
                                  setCustomFieldsDefaults((prev) => ({
                                    ...prev,
                                    [cf.id]: newV === "__empty__" ? "" : newV,
                                  }))
                                }
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Padrão (opcional)..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem
                                    value="__empty__"
                                    className="text-xs text-muted-foreground"
                                  >
                                    Nenhum / Em branco
                                  </SelectItem>
                                  {(cf.options || []).map((opt) => (
                                    <SelectItem
                                      key={opt}
                                      value={opt}
                                      className="text-xs"
                                    >
                                      {opt}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                value={val}
                                onChange={(e) =>
                                  setCustomFieldsDefaults((prev) => ({
                                    ...prev,
                                    [cf.id]: e.target.value,
                                  }))
                                }
                                placeholder="Valor padrão (opcional)..."
                                className="h-8 text-xs"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Duplicate strategy */}
            <div className="p-4 rounded-lg bg-card border border-border/70 space-y-3">
              <h3 className="text-xs font-semibold text-foreground">
                Como tratar registros duplicados?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(
                  [
                    {
                      value: "merge" as DuplicateStrategy,
                      label: "Mesclar e Atualizar",
                      desc: "(Recomendado) Se encontrar o mesmo E-mail ou CNPJ, atualiza os dados sem criar duplicatas.",
                    },
                    {
                      value: "create_new" as DuplicateStrategy,
                      label: "Criar Novos",
                      desc: "Cria sempre um novo registro mesmo se o contato ou empresa já existirem no banco.",
                    },
                    {
                      value: "skip" as DuplicateStrategy,
                      label: "Ignorar Duplicatas",
                      desc: "Se o contato ou empresa já existirem, não altera os dados existentes e segue adiante.",
                    },
                  ] as const
                ).map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => setDuplicateStrategy(opt.value)}
                    className={`p-3 rounded-lg border text-left cursor-pointer transition-all ${
                      duplicateStrategy === opt.value
                        ? "border-primary bg-primary/5 shadow-xs"
                        : "border-border/70 hover:bg-muted/40"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-foreground">
                        {opt.label}
                      </span>
                      {duplicateStrategy === opt.value && (
                        <Check className="w-3.5 h-3.5 text-primary" />
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {opt.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="pt-2 flex items-center justify-between border-t border-border/50">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep("mapping")}
                className="text-xs gap-1.5 cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Voltar ao Mapeamento
              </Button>
              <Button
                size="sm"
                onClick={handleStartImport}
                className="text-xs gap-1.5 cursor-pointer font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Play className="w-3.5 h-3.5" />
                Iniciar Importação de{" "}
                {parsedData.totalRows.toLocaleString("pt-BR")} Linhas
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ETAPA 4: EXECUÇÃO                                                   */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {step === "processing" && (
        <Card className="border-border/80 shadow-xs">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-lg font-bold">
              Importando Dados...
            </CardTitle>
            <CardDescription className="text-xs">
              Por favor, aguarde. Estamos gravando os registros e criando os
              vínculos entre Pessoa, Empresa e Oportunidades.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-medium">
                <span>{progress?.statusText || "Gravando registros..."}</span>
                <span className="font-bold text-primary">
                  {progress?.percent || 0}%
                </span>
              </div>
              <Progress value={progress?.percent || 0} className="h-3" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="p-3 rounded-lg border bg-card">
                <span className="text-xs text-muted-foreground block">
                  Organizações
                </span>
                <span className="text-xl font-bold text-blue-600">
                  {(progress?.companiesCreated || 0) +
                    (progress?.companiesUpdated || 0)}
                </span>
              </div>
              <div className="p-3 rounded-lg border bg-card">
                <span className="text-xs text-muted-foreground block">
                  Pessoas / Contatos
                </span>
                <span className="text-xl font-bold text-emerald-600">
                  {(progress?.contactsCreated || 0) +
                    (progress?.contactsUpdated || 0)}
                </span>
              </div>
              <div className="p-3 rounded-lg border bg-card">
                <span className="text-xs text-muted-foreground block">
                  Oportunidades
                </span>
                <span className="text-xl font-bold text-amber-600">
                  {progress?.dealsCreated || 0}
                </span>
              </div>
              <div className="p-3 rounded-lg border bg-card">
                <span className="text-xs text-muted-foreground block">
                  Linhas Ignoradas
                </span>
                <span className="text-xl font-bold text-rose-600">
                  {progress?.skippedCount || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* ETAPA 5: CONCLUSÃO                                                  */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      {step === "completed" && result && (
        <Card className="border-border/80 shadow-xs">
          <CardHeader className="text-center pb-3">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 mx-auto flex items-center justify-center mb-2">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <CardTitle className="text-xl font-bold">
              Importação Finalizada!
            </CardTitle>
            <CardDescription className="text-xs">
              Sua planilha foi processada e os dados já estão disponíveis no CRM
              Dotus.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="p-3 rounded-lg border bg-muted/20">
                <span className="text-xs text-muted-foreground block">
                  Empresas Criadas
                </span>
                <span className="text-lg font-bold text-foreground">
                  {result.companiesCreated}
                </span>
              </div>
              <div className="p-3 rounded-lg border bg-muted/20">
                <span className="text-xs text-muted-foreground block">
                  Contatos Criados
                </span>
                <span className="text-lg font-bold text-foreground">
                  {result.contactsCreated}
                </span>
              </div>
              <div className="p-3 rounded-lg border bg-muted/20">
                <span className="text-xs text-muted-foreground block">
                  Negócios no Kanban
                </span>
                <span className="text-lg font-bold text-foreground">
                  {result.dealsCreated}
                </span>
              </div>
              <div className="p-3 rounded-lg border bg-muted/20">
                <span className="text-xs text-muted-foreground block">
                  Registros Atualizados
                </span>
                <span className="text-lg font-bold text-foreground">
                  {result.companiesUpdated + result.contactsUpdated}
                </span>
              </div>
            </div>

            {result.hasErrors && (
              <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-start gap-2.5 text-left">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-xs text-foreground block">
                      {result.skippedCount}{" "}
                      {result.skippedCount === 1
                        ? "linha foi ignorada"
                        : "linhas foram ignoradas"}
                    </strong>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Baixe o relatório com o motivo de cada erro para corrigir
                      e reimportar facilmente.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadSkipFile}
                  className="text-xs gap-1.5 shrink-0 bg-background border-amber-500/40 text-amber-800 dark:text-amber-300 hover:bg-amber-500/20 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  Baixar Arquivo de Ignorados (.csv)
                </Button>
              </div>
            )}

            <div className="flex items-center justify-center gap-3 pt-3 flex-wrap">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="text-xs cursor-pointer"
              >
                <Link to="/deals">Ver Negócios no Kanban</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="text-xs cursor-pointer"
              >
                <Link to="/contacts">Ver Lista de Contatos</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="text-xs cursor-pointer"
              >
                <Link to="/companies">Ver Organizações</Link>
              </Button>
              <Button
                size="sm"
                onClick={handleResetWizard}
                className="text-xs cursor-pointer font-semibold"
              >
                Importar Outra Planilha
              </Button>
              {result.rollbackInfo && result.rollbackInfo.totalCreated > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setUndoTargetBatch(result.rollbackInfo || lastImport)
                  }
                  disabled={isRollbackUndoing}
                  className="text-xs cursor-pointer font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 border-rose-300 dark:border-rose-900/50 gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Desfazer Esta Importação
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {undoTargetBatch && (
        <Dialog
          open={Boolean(undoTargetBatch)}
          onOpenChange={(open) => {
            if (!open) setUndoTargetBatch(null);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-2 text-rose-600 mb-1">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <DialogTitle>Desfazer importação?</DialogTitle>
              </div>
              <DialogDescription className="text-xs text-muted-foreground pt-1 space-y-2">
                <p>
                  Deseja realmente cancelar e desfazer a importação da planilha{" "}
                  <strong className="text-foreground">
                    {undoTargetBatch.fileName}
                  </strong>
                  ?
                </p>
                <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-foreground space-y-1 my-2">
                  <span className="font-semibold block text-[11px] text-rose-700 dark:text-rose-400">
                    Os seguintes registros criados serão removidos:
                  </span>
                  <ul className="list-disc list-inside text-[11px] space-y-0.5 text-muted-foreground">
                    {undoTargetBatch.dealIds.length > 0 && (
                      <li>
                        <strong className="text-foreground">
                          {undoTargetBatch.dealIds.length}
                        </strong>{" "}
                        Oportunidade(s) / Negócio(s) no Kanban
                      </li>
                    )}
                    {undoTargetBatch.contactIds.length > 0 && (
                      <li>
                        <strong className="text-foreground">
                          {undoTargetBatch.contactIds.length}
                        </strong>{" "}
                        Contato(s) criado(s)
                      </li>
                    )}
                    {undoTargetBatch.companyIds.length > 0 && (
                      <li>
                        <strong className="text-foreground">
                          {undoTargetBatch.companyIds.length}
                        </strong>{" "}
                        Organização(ões) criada(s)
                      </li>
                    )}
                  </ul>
                </div>
                <p className="text-[11px] text-rose-600 dark:text-rose-400 font-medium">
                  Esta ação não pode ser revertida.
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0 mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUndoTargetBatch(null)}
                disabled={isRollbackUndoing}
                className="cursor-pointer text-xs"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleConfirmUndo}
                disabled={isRollbackUndoing}
                className="cursor-pointer text-xs gap-1.5 font-semibold bg-rose-600 hover:bg-rose-700 text-white"
              >
                {isRollbackUndoing ? (
                  <Sparkles className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="w-3.5 h-3.5" />
                )}
                Sim, Desfazer Importação
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
